import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Search, ChevronDown } from 'lucide-react';
const FAQ_DATA = [{
  category: 'General',
  questions: [{
    q: 'What is Booker?',
    a: 'Booker is a booking operating system for live performance. It helps artists and bands plan tours, find venues, send outreach, negotiate deals, manage contracts and deposits, lock lineups, and run confirmed shows. It also helps venues fill dates and agents manage a roster from one shared workflow.'
  }, {
    q: 'Who is Booker for?',
    a: 'Booker is built for three main groups: artists/bands, venues/promoters, and agents/managers. Each gets a role-native experience on top of the same booking workflow engine.'
  }, {
    q: 'Is Booker a marketplace?',
    a: 'Booker is primarily a workflow platform, not a consumer ticket marketplace. It is designed to help both sides of live booking discover the right fit, manage outreach, negotiate, contract, confirm, and operate shows.'
  }, {
    q: 'Is Booker a replacement for a human booking agent?',
    a: 'Not by default. Booker is software-first. It helps users organize and automate the work around booking, but important approvals and decisions stay in human hands unless explicitly configured otherwise within platform rules.'
  }, {
    q: 'What makes Booker different from using spreadsheets, email, and shared calendars?',
    a: 'Booker ties discovery, outreach, offers, holds, contracts, deposits, lineup dependencies, and show operations into one workflow. Instead of juggling multiple tools, users can see the current state of a booking from first contact to final settlement.'
  }, {
    q: 'Does Booker work for one-off bookings and full tours?',
    a: 'Yes. Booker is designed to support one-off dates, regional runs, routed tours, multi-date events, residencies, and repeat venue programming.'
  }, {
    q: 'Can Booker be used by more than one type of user in the same ecosystem?',
    a: 'Yes. Artists, venues, and agents can all use Booker. The experience changes by role, but the underlying workflow stays shared so both sides of a booking can move through the same core process.'
  }]
}, {
  category: 'Artists & Bands',
  questions: [{
    q: 'How does Booker help artists or bands get booked?',
    a: 'Booker helps artists define a campaign, generate routes, find matching venues, identify contacts, draft outreach, manage replies, evaluate offers, send contracts, request deposits, and carry confirmed shows into itinerary and logistics.'
  }, {
    q: 'Can Booker help me plan a regional tour?',
    a: 'Yes. You can define a date range, region, routing rules, capacity targets, fee range, and other constraints. Booker can then suggest route options and help identify venues that fit the run.'
  }, {
    q: 'Can I use Booker if I do not have a manager or agent?',
    a: 'Yes. Booker is designed to support self-managed artists and bands as well as professional managers and agents.'
  }, {
    q: 'Does Booker include a public booking page or EPK?',
    a: 'Yes. Booker includes an interactive booking page / EPK surface so artists can present media, booking details, and inquiry options in a more structured way.'
  }, {
    q: 'Can I upload riders, contracts, and promotional assets?',
    a: 'Yes. Booker includes an asset and document layer for storing and reusing contracts, riders, promo materials, and booking documents.'
  }, {
    q: 'Can Booker help me keep track of where I already played?',
    a: 'Yes. Booker is designed to maintain show history and venue history so artists and managers can track past markets, relationships, and prior activity.'
  }, {
    q: 'Can band members have limited access?',
    a: 'Yes. Booker supports role-based access and limited member views so people can see what they need for scheduling, lineup confirmation, itinerary, or payout visibility without exposing the entire booking workflow.'
  }, {
    q: 'Can band members be hidden from total deal value?',
    a: 'Yes. Booker is designed to support private member compensation visibility so a member can see their own agreed terms without necessarily seeing the total show value or what other members are paid.'
  }]
}, {
  category: 'Venues & Promoters',
  questions: [{
    q: 'How does Booker help venues?',
    a: 'Booker helps venues manage open dates, define event needs, discover artists by fit and availability, compare options, send offers, track holds, manage contracts, and keep show programming organized.'
  }, {
    q: 'Can Booker help fill an open date quickly?',
    a: 'Yes. A venue can create a structured event need with a date, room, budget, and genre target. Booker can then surface matching artists so the venue team can shortlist and act quickly.'
  }, {
    q: 'Can venues search for artists by date and budget?',
    a: 'Yes. Booker is designed to support filtering by availability, genre, fee band, room-size fit, geography, and other constraints.'
  }, {
    q: 'Can multiple venue team members collaborate in Booker?',
    a: 'Yes. Venue workflows can support role-based permissions, assignments, approval flows, and shared communication visibility.'
  }, {
    q: 'Can a venue manage multiple rooms or recurring programming?',
    a: 'Yes. Booker is intended to support room-based programming, recurring event slots, open-date workflows, and room-level scheduling logic.'
  }, {
    q: 'Can venues respond to offers or booking inquiries from email?',
    a: 'That is part of the planned venue-side workflow. Booker is designed to make it easier for venues to review, respond, and move quickly, including lightweight approval and comment flows.'
  }]
}, {
  category: 'Agents & Managers',
  questions: [{
    q: 'How is Booker useful for agents and managers?',
    a: 'Booker gives agents and managers a roster-level view of campaigns, opportunities, deals, contracts, payments, and team assignments. It is designed to make high-volume booking work more visible and repeatable.'
  }, {
    q: 'Can I manage multiple artists in Booker?',
    a: 'Yes. Booker includes a roster model so managers and agencies can track multiple artists, multiple campaigns, and multiple active deals at once.'
  }, {
    q: 'Does Booker support team collaboration?',
    a: 'Yes. Booker is built to support team members with different roles, scopes, and permissions, including inbox collaboration, approvals, and workflow ownership.'
  }, {
    q: 'Can Booker standardize how we negotiate and contract deals?',
    a: 'Yes. Booker includes policy, template, and workflow concepts intended to help teams standardize outreach, approvals, contract generation, and financial follow-through.'
  }, {
    q: 'Can Booker help us see what is stuck or at risk?',
    a: "Yes. One of Booker's core goals is to make blocked deals, route gaps, lineup conflicts, missing deposits, and other operational risks visible earlier."
  }]
}, {
  category: 'Deals, Contracts & Payments',
  questions: [{
    q: 'Can Booker handle offers, counters, and holds?',
    a: 'Yes. Booker is designed to support a structured deal workflow including offer capture, counter logic, hold tracking, expiration states, and confirmation milestones.'
  }, {
    q: 'Can Booker generate contracts?',
    a: 'Yes. Booker is designed to support reusable templates, structured term insertion, clause presets, and generated booking documents.'
  }, {
    q: 'Can Booker support e-signatures?',
    a: 'Yes. E-sign workflow support is part of the product plan so deals can move into executed documents without leaving the platform flow.'
  }, {
    q: 'Can Booker send invoices and request deposits?',
    a: 'Yes. Booker is intended to support invoice generation, deposit requests, reminders, and payment state tracking.'
  }, {
    q: 'Does Booker support payment plans?',
    a: 'Yes. Booker is designed to support deposit/balance logic and more flexible payment structures where needed, including multi-date scenarios.'
  }, {
    q: 'Can Booker track settlement after the show?',
    a: 'Yes. Booker is designed to carry the workflow through settlement and show-level financial closure, not just the booking moment.'
  }, {
    q: 'Can off-platform payments still be recorded?',
    a: 'Yes. Booker is expected to support manual/off-platform payment recording and proof workflows so the operational record stays complete even when money moves outside the platform rails.'
  }]
}, {
  category: 'Lineup & Scheduling',
  questions: [{
    q: 'Can Booker manage band-member availability?',
    a: "Yes. Booker is designed to support lineup-aware workflows where a band's required personnel must be available before a show is treated as fully locked."
  }, {
    q: 'What happens if band members are in multiple bands?',
    a: 'Booker is intended to support conflict-aware scheduling so one person can be linked to multiple acts and potential scheduling conflicts can be flagged early.'
  }, {
    q: 'Can Booker handle alternate players or substitutes?',
    a: 'Yes. Booker is designed to support substitute or alternate musician logic where a role can be fulfilled by approved backup personnel when appropriate.'
  }, {
    q: 'What does "fully locked" mean in Booker?',
    a: 'That depends on the configured rules, but Booker is designed to distinguish between a venue saying yes and a show being fully locked. A fully locked show may require some combination of accepted terms, contract completion, deposit status, and lineup confirmation.'
  }, {
    q: 'Can I see holds, confirmed shows, and blackout dates together?',
    a: 'Yes. Booker is designed around a shared scheduling view that can combine availability, holds, confirmed shows, deadlines, and other date-sensitive information.'
  }]
}, {
  category: 'AI Assistant',
  questions: [{
    q: 'What does the AI assistant do?',
    a: 'The AI assistant helps users search, plan, summarize, draft, and prepare actions across Booker. It can help with routes, venue discovery, outreach drafts, offer summaries, lineup checks, and document preparation.'
  }, {
    q: 'Is the AI assistant a separate product?',
    a: 'No. The AI assistant is an alternate control surface on top of the same structured product. The goal is to let users work through normal pages or through conversation, with the same underlying workflow and approval rules.'
  }, {
    q: 'Can I use Booker without the AI assistant?',
    a: 'Yes. Booker should work through the standard interface alone. The AI assistant is meant to speed up work, not make the UI optional.'
  }, {
    q: 'Can the AI send emails or make booking decisions on its own?',
    a: 'Only within configured rules and approval boundaries. Booker is intended to preserve human control at important decision points such as accepting a deal, locking a show, or approving sensitive financial actions.'
  }, {
    q: 'Can the AI help with phone-based workflows?',
    a: 'That is part of the broader product direction, especially for narrow follow-up or information-gathering workflows, but it should remain tightly controlled and policy-driven.'
  }, {
    q: 'Will chat and the normal interface stay in sync?',
    a: 'Yes. Booker is designed so the chat interface and structured UI operate on the same underlying objects and workflow states.'
  }]
}, {
  category: 'Setup, Access & Security',
  questions: [{
    q: 'Is Booker self-serve?',
    a: 'Yes. Booker is intended to support self-serve onboarding, especially for artists, smaller teams, and users who want to get to value quickly without a sales-led setup.'
  }, {
    q: 'Can I invite teammates?',
    a: 'Yes. Booker is designed to support organizations, teammates, role-based access, and permission-scoped collaboration.'
  }, {
    q: 'Can different users have different permissions?',
    a: 'Yes. Role-based access and scoped permissions are core to the product model, especially because not every user should see the same financial or operational detail.'
  }, {
    q: 'Does Booker support two-factor authentication?',
    a: 'That is part of the broader account and security planning, along with role and session control.'
  }, {
    q: 'Will Booker keep an audit trail of sensitive actions?',
    a: 'Yes. Booker is intended to record sensitive workflow actions, approvals, and changes so teams have better operational accountability.'
  }, {
    q: 'Can I connect my calendar and email?',
    a: 'Yes. Calendar and inbox integrations are core parts of the product experience because availability and communication are central to booking.'
  }]
}, {
  category: 'Pricing & Plans',
  questions: [{
    q: 'Will Booker have a free plan?',
    a: 'The product is being designed with self-serve usage in mind. Final plan structure can vary, but the goal is to let users experience real booking value before needing a larger commitment.'
  }, {
    q: 'How will pricing likely work?',
    a: 'Pricing is likely to vary by role, workflow volume, automation level, and team complexity rather than simply by seat count alone.'
  }, {
    q: 'Will artists, venues, and agents have different plan structures?',
    a: 'Possibly. Since the product supports different ICPs with different workflow volumes and needs, packaging may reflect those differences.'
  }, {
    q: 'What kinds of features would likely be part of higher plans?',
    a: 'Higher plans may include more campaigns or event needs, more workflow automation, deeper contract and payment tools, team collaboration, roster or venue-team controls, and more advanced analytics.'
  }, {
    q: 'Can larger teams talk to sales instead of self-serving?',
    a: 'Yes. Booker should support both self-serve and higher-touch buying motions depending on customer needs.'
  }]
}];
export function FAQPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(FAQ_DATA[0].category);
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);
  // Filter logic
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return FAQ_DATA;
    const query = searchQuery.toLowerCase();
    return FAQ_DATA.map(cat => ({
      ...cat,
      questions: cat.questions.filter(q => q.q.toLowerCase().includes(query) || q.a.toLowerCase().includes(query))
    })).filter(cat => cat.questions.length > 0);
  }, [searchQuery]);
  // If searching, we don't use the sidebar category filter
  const displayData = searchQuery ? filteredData : FAQ_DATA.filter(c => c.category === activeCategory);
  return <div className="min-h-screen pt-32 pb-24" data-id="element-6149">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 mb-16 text-center" data-id="element-6150">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-4xl md:text-5xl font-bold text-textMain mb-6 tracking-tight" data-id="element-6151">
          Questions about Booker? Start here.
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-lg text-textMuted leading-relaxed max-w-3xl mx-auto" data-id="element-6152">
          Everything you need to understand how Booker works for artists,
          venues, agents, contracts, payments, lineup management, and
          AI-assisted booking workflows.
        </motion.p>
      </section>

      {/* Search Bar */}
      <section className="max-w-3xl mx-auto px-6 mb-12" data-id="element-6153">
        <div className="relative" data-id="element-6154">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-textMuted" data-id="element-6155" />
          <input type="text" placeholder="Search for answers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-surface border border-border rounded-xl text-textMain focus:outline-none focus:border-primary transition-colors text-lg" data-id="element-6156" />
        </div>
      </section>

      {/* Main Content Area */}
      <section className="max-w-6xl mx-auto px-6 mb-24 flex flex-col md:flex-row gap-12" data-id="element-6157">
        {/* Category Nav (Desktop Sticky, Mobile Scrollable) */}
        {!searchQuery && <div className="md:w-64 shrink-0" data-id="element-6158">
            <div className="sticky top-32 flex md:flex-col gap-2 overflow-x-auto pb-4 md:pb-0 no-scrollbar" data-id="element-6159">
              {FAQ_DATA.map(cat => <button key={cat.category} onClick={() => setActiveCategory(cat.category)} className={`text-left px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeCategory === cat.category ? 'bg-primary/10 text-primary' : 'text-textMuted hover:text-textMain hover:bg-surface'}`} data-id="element-6160">
                  {cat.category}
                </button>)}
            </div>
          </div>}

        {/* FAQ Accordions */}
        <div className="flex-1" data-id="element-6161">
          {displayData.length === 0 ? <div className="text-center py-12 text-textMuted" data-id="element-6162">
              No results found for "{searchQuery}"
            </div> : <div className="space-y-8" data-id="element-6163">
              {displayData.map(cat => <div key={cat.category} data-id="element-6164">
                  {searchQuery && <h2 className="text-xl font-bold text-textMain mb-4" data-id="element-6165">
                      {cat.category}
                    </h2>}
                  <div className="space-y-3" data-id="element-6166">
                    {cat.questions.map((item, i) => {
                const isOpen = openQuestion === item.q;
                return <div key={i} className="bg-surface border border-border rounded-xl overflow-hidden" data-id="element-6167">
                          <button onClick={() => setOpenQuestion(isOpen ? null : item.q)} className="w-full flex items-center justify-between p-5 text-left focus:outline-none" data-id="element-6168">
                            <span className="font-medium text-textMain pr-8" data-id="element-6169">
                              {item.q}
                            </span>
                            <ChevronDown className={`w-5 h-5 text-textMuted shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-6170" />
                          </button>
                          <AnimatePresence data-id="element-6171">
                            {isOpen && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.3,
                      ease: 'easeInOut'
                    }} data-id="element-6172">
                                <div className="p-5 pt-0 text-textMuted leading-relaxed border-t border-border/50" data-id="element-6173">
                                  {item.a}
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>;
              })}
                  </div>
                </div>)}
            </div>}
        </div>
      </section>

      {/* CTA Band */}
      <section className="max-w-4xl mx-auto px-6 text-center" data-id="element-6174">
        <div className="bg-surface border border-border rounded-2xl p-12" data-id="element-6175">
          <h2 className="text-3xl font-bold text-textMain mb-4" data-id="element-6176">
            Still have questions?
          </h2>
          <p className="text-textMuted mb-8 max-w-2xl mx-auto" data-id="element-6177">
            Whether you are trying to book a first regional run, fill open venue
            dates, or manage a full roster, we can help you figure out if Booker
            fits your workflow.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-6178">
            <Link to="/onboarding" className="px-8 py-3 bg-primary text-background font-bold rounded-lg hover:bg-primaryHover transition-colors" data-id="element-6179">
              Start free
            </Link>
            <Link to="/demo" className="px-8 py-3 bg-surface border border-border text-textMain font-medium rounded-lg hover:bg-surfaceHover transition-colors" data-id="element-6180">
              Book a demo
            </Link>
            <Link to="/contact-sales" className="px-8 py-3 bg-surface border border-border text-textMain font-medium rounded-lg hover:bg-surfaceHover transition-colors" data-id="element-6181">
              Contact sales
            </Link>
          </div>
        </div>
      </section>
    </div>;
}